import { action, computed, makeObservable, observable } from 'mobx'
import { initAssistant } from '../api/ai-assistant'
import { MessageRole } from '../model/ai-assistant.model'
import type {
  AiAssistantAction,
  AiAssistantConversationType,
  AiAssistantInitRequest,
  AiAssistantInitResponse,
  AiAssistantLanguage,
  AiAssistantUserType,
  AssistantMessage,
} from '../model/ai-assistant.model'
import { generateUniqId } from 'src/model/Id'
import { localizationStoreInstance } from 'src/feature/localization/store/localization.store'
import { userStoreInstance } from 'src/feature/user/store/user.store'
import { authStore } from 'src/feature/auth/store/auth.store'
import { OrganisationRole } from 'src/feature/control-panel/model/OrganisationRole'

type AssistantConversationState = {
  messages: Array<Omit<AssistantMessage, 'timestamp'> & { timestamp: string }>
  suggestedPrompts: string[]
  actions: AiAssistantAction[]
}

const STORAGE_KEY_PREFIX = 'YohrsAiAssistant'

export class AiAssistantStore {
  isOpen = false
  messages: AssistantMessage[] = []
  isTyping = false
  suggestedPrompts: string[] = []
  actions: AiAssistantAction[] = []
  errorMessage: string | null = null

  private hydratedStorageKey: string | null = null
  private hasAttemptedGreeting = false
  private lastRequest: AiAssistantInitRequest | null = null

  constructor() {
    makeObservable(this, {
      isOpen: observable,
      messages: observable,
      isTyping: observable,
      suggestedPrompts: observable,
      actions: observable,
      errorMessage: observable,
      messageCount: computed,
      open: action,
      close: action,
      toggle: action,
      ensureConversation: action,
      syncConversation: action,
      sendMessage: action,
      initializeGreeting: action,
      retryLastRequest: action,
      clearMessages: action,
    })
  }

  get messageCount() {
    return this.messages.filter(message => !message.isGreeting).length
  }

  open() {
    this.isOpen = true
  }

  close() {
    this.isOpen = false
  }

  toggle() {
    this.isOpen = !this.isOpen
  }

  async ensureConversation() {
    this.syncConversation()

    if (!this.messages.length && !this.isTyping && !this.errorMessage && !this.hasAttemptedGreeting) {
      this.hasAttemptedGreeting = true
      await this.initializeGreeting()
    }
  }

  syncConversation() {
    const storageKey = this.getStorageKey()

    if (this.hydratedStorageKey === storageKey) {
      return
    }

    this.hydratedStorageKey = storageKey
    this.loadConversation()
    this.errorMessage = null
    this.lastRequest = null
    this.hasAttemptedGreeting = this.messages.length > 0
  }

  async initializeGreeting() {
    await this.submitMessage('', false, 'skill')
  }

  async sendMessage(message: string, conversationType: AiAssistantConversationType) {
    const trimmedMessage = message.trim()

    if (!trimmedMessage || this.isTyping) {
      return
    }

    await this.submitMessage(trimmedMessage, true, conversationType)
  }

  async retryLastRequest() {
    if (!this.lastRequest || this.isTyping) {
      return
    }

    await this.submitMessage(
      this.lastRequest.message ?? '',
      false,
      this.lastRequest.conversationType,
    )
  }

  clearMessages() {
    this.messages = []
    this.suggestedPrompts = []
    this.actions = []
    this.errorMessage = null
    this.lastRequest = null
    this.hasAttemptedGreeting = false
    this.removeConversation()
  }

  private async submitMessage(
    message: string,
    appendUserMessage: boolean,
    conversationType: AiAssistantConversationType,
  ) {
    const request = this.buildRequest(message, conversationType)
    const isGreetingRequest = !message

    this.errorMessage = null
    this.lastRequest = request

    if (appendUserMessage && !isGreetingRequest) {
      const userMessage: AssistantMessage = {
        id: generateUniqId(),
        role: MessageRole.User,
        content: message,
        timestamp: new Date(),
      }

      this.messages.push(userMessage)
      this.persistConversation()
    }

    this.isTyping = true

    try {
      const response = await initAssistant(request)
      this.applyResponse(response, isGreetingRequest)
      this.hasAttemptedGreeting = true
    } catch {
      this.errorMessage = localizationStoreInstance.translation.pages.myDashboard.aiAssistant.requestFailedBody()
    } finally {
      this.isTyping = false
      this.persistConversation()
    }
  }

  private applyResponse(response: AiAssistantInitResponse, isGreetingRequest: boolean) {
    const assistantMessage: AssistantMessage = {
      id: generateUniqId(),
      role: MessageRole.Assistant,
      content: response.answer,
      timestamp: new Date(),
      isGreeting: isGreetingRequest,
    }

    this.messages.push(assistantMessage)
    this.suggestedPrompts = response.suggestedPrompts
    this.actions = response.actions ?? []
  }

  private buildRequest(
    message: string,
    conversationType: AiAssistantConversationType,
  ): AiAssistantInitRequest {
    return {
      userType: this.getUserType(),
      conversationType,
      language: this.getLanguage(),
      message,
    }
  }

  private getLanguage(): AiAssistantLanguage {
    return localizationStoreInstance.locale === 'sv-SE' ? 'sv' : 'en'
  }

  private getUserType(): AiAssistantUserType {
    const isManager = authStore.hasRole(OrganisationRole.UserAdministrator)

    return isManager ? 'user_manager' : 'user'
  }

  private getStorageKey() {
    const userId = userStoreInstance.userData?.id ?? 'anonymous'

    return `${STORAGE_KEY_PREFIX}:${userId}`
  }

  private getLegacyStorageKeys() {
    const storageKey = this.getStorageKey()
    const currentLanguage = this.getLanguage()
    const alternateLanguage: AiAssistantLanguage = currentLanguage === 'en' ? 'sv' : 'en'

    return [currentLanguage, alternateLanguage].map(language => `${storageKey}:${language}`)
  }

  private loadConversation() {
    if (typeof window === 'undefined') {
      return
    }

    const storageKey = this.getStorageKey()
    const currentValue = window.localStorage.getItem(storageKey)
    const legacyStorageKeys = this.getLegacyStorageKeys()
    const rawValue = currentValue ?? legacyStorageKeys
      .map(key => window.localStorage.getItem(key))
      .find((value): value is string => Boolean(value))

    if (!rawValue) {
      this.messages = []
      this.suggestedPrompts = []
      this.actions = []

      return
    }

    try {
      const parsedValue = JSON.parse(rawValue) as AssistantConversationState

      this.messages = (parsedValue.messages ?? []).map(message => ({
        ...message,
        timestamp: new Date(message.timestamp),
      }))
      this.suggestedPrompts = parsedValue.suggestedPrompts ?? []
      this.actions = parsedValue.actions ?? []

      if (!currentValue) {
        window.localStorage.setItem(storageKey, rawValue)
        legacyStorageKeys.forEach(key => window.localStorage.removeItem(key))
      }
    } catch {
      this.messages = []
      this.suggestedPrompts = []
      this.actions = []
    }
  }

  private persistConversation() {
    if (typeof window === 'undefined') {
      return
    }

    const state: AssistantConversationState = {
      messages: this.messages.map(message => ({
        ...message,
        timestamp: message.timestamp.toISOString(),
      })),
      suggestedPrompts: this.suggestedPrompts,
      actions: this.actions,
    }

    window.localStorage.setItem(this.getStorageKey(), JSON.stringify(state))
  }

  private removeConversation() {
    if (typeof window === 'undefined') {
      return
    }

    window.localStorage.removeItem(this.getStorageKey())
    this.getLegacyStorageKeys().forEach(key => window.localStorage.removeItem(key))
  }
}

export const aiAssistantStore = new AiAssistantStore()

export const useAiAssistantStore = () => aiAssistantStore
